import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Tabs, Tab, CircularProgress, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { siteService } from 'components/api/sites';
import { PointCategory, PointSubCategory } from 'types/point';
import { commonGridStyles } from 'styles/gridStyles';
import { Alert } from 'components/alert/alert';
import ConfirmModal from 'components/modals/ConfirmModal';
import { formatDate } from 'utils/date';
import styles from './PointConfig.module.scss';

interface PointConfigProps {
  siteId: string | number;
}

const PointConfig: React.FC<PointConfigProps> = ({ siteId }) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<PointCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | ''>('');

  const [subCategories, setSubCategories] = useState<PointSubCategory[]>([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);

  const [editingSubId, setEditingSubId] = useState<number | null>(null);
  const [editSubData, setEditSubData] = useState<Partial<PointSubCategory> | null>(null);
  const [savingSub, setSavingSub] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: 'subcategory' | null;
    originalRow?: PointSubCategory;
  }>({ open: false, type: null });

  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showFeedback = useCallback((message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setFeedback({ message, type });
    timeoutRef.current = setTimeout(() => {
      setFeedback(null);
      timeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteService.getPointCategories(siteId);
      // Depending on API structure, it might be response.data or just response
      // We will handle the array extraction.
      const data = response?.data || response;
      const cats: PointCategory[] = Array.isArray(data) ? data : data?.data || [];

      const filterdCats = cats.filter((c) => c.key !== 'sport');
      setCategories(filterdCats);

      if (filterdCats.length > 0) {
        setActiveCategoryId(filterdCats[0].id);
      }
    } catch (error: any) {
      console.error('Failed to fetch point categories:', error);
      const message =
        error.response?.data?.message || error.message || 'Failed to load point categories';
      showFeedback(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const fetchSubCategories = useCallback(async (categoryId: number) => {
    try {
      setSubCategoriesLoading(true);
      const response = await siteService.getPointSubCategories(categoryId);
      // It might return subcategories array directly or inside a field
      const data = response?.data || response;
      const subs: PointSubCategory[] = Array.isArray(data)
        ? data
        : data?.subCategories || data?.data || [];
      setSubCategories(subs);
    } catch (error: any) {
      console.error('Failed to fetch point subcategories:', error);
      const message =
        error.response?.data?.message || error.message || 'Failed to load subcategories';
      showFeedback(message, 'error');
    } finally {
      setSubCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (activeCategoryId !== '') {
      fetchSubCategories(activeCategoryId);
    }
  }, [activeCategoryId, fetchSubCategories]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveCategoryId(newValue);
    setFeedback(null);
    setEditingSubId(null);
    setEditSubData(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleEditClick = (row: PointSubCategory) => {
    setEditingSubId(row.id as number);
    setEditSubData(row);
  };

  const handleCancelClick = () => {
    setEditingSubId(null);
    setEditSubData(null);
  };

  const handleRowChange = (field: keyof PointSubCategory, value: any) => {
    setEditSubData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const executeSaveRow = async (originalRow: PointSubCategory) => {
    if (!editSubData) return;
    try {
      setSavingSub(true);
      await siteService.updatePointSubCategory(originalRow.id, {
        name: editSubData.name as string,
        multiplier: editSubData.multiplier as string | number,
        isActive: editSubData.isActive as boolean,
      });
      showFeedback('Subcategory updated successfully', 'success');

      // Optimistic update
      setSubCategories((prev) =>
        prev.map((item) => (item.id === originalRow.id ? { ...item, ...editSubData } : item)),
      );

      setEditingSubId(null);
      setEditSubData(null);
      setConfirmModal({ open: false, type: null });
    } catch (error: any) {
      console.error('Failed to update subcategory:', error);
      const message =
        error.response?.data?.message || error.message || 'Failed to update subcategory';
      showFeedback(message, 'error');
      setConfirmModal({ open: false, type: null });
    } finally {
      setSavingSub(false);
    }
  };

  const handleSaveRowClick = (originalRow: PointSubCategory) => {
    setConfirmModal({ open: true, type: 'subcategory', originalRow });
  };

  const confirmSave = () => {
    if (confirmModal.type === 'subcategory' && confirmModal.originalRow) {
      executeSaveRow(confirmModal.originalRow);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 90, sortable: false },
      {
        field: 'name',
        headerName: 'Name',
        width: 250,
        sortable: false,
      },
      {
        field: 'multiplier',
        headerName: 'Multiplier',
        width: 150,
        sortable: false,
        renderCell: (params) => {
          if (editingSubId === params.row.id) {
            return (
              <input
                type="text"
                className={styles.cellInput}
                value={editSubData?.multiplier ?? ''}
                onChange={(e) => handleRowChange('multiplier', e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            );
          }
          return params.value;
        },
      },
      {
        field: 'isActive',
        headerName: 'Active',
        width: 120,
        sortable: false,
        renderCell: (params) => (params.value ? 'Yes' : 'No'),
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        width: 180,
        sortable: false,
        valueFormatter: (value: any) => (value ? formatDate(value) : '-'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        renderCell: (params) => {
          if (editingSubId === params.row.id) {
            const isDirty =
              editSubData?.name !== params.row.name ||
              editSubData?.multiplier !== params.row.multiplier ||
              editSubData?.isActive !== params.row.isActive;

            return (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  className={styles.actionBtn}
                  onClick={handleCancelClick}
                  disabled={savingSub}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={styles.actionBtn}
                  onClick={() => handleSaveRowClick(params.row)}
                  disabled={!isDirty || savingSub}
                  sx={{
                    backgroundColor: 'var(--accent-blue)',
                    '&:hover': { backgroundColor: '#0092c7' },
                  }}
                >
                  Save
                </Button>
              </Box>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                className={styles.actionBtn}
                onClick={() => handleEditClick(params.row)}
                sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
              >
                Edit
              </Button>
            </Box>
          );
        },
      },
    ],
    [editingSubId, editSubData, savingSub],
  );

  if (loading) {
    return (
      <Box className={styles.loadingWrapper}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="textSecondary">No point categories found for this site.</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Alert message={feedback?.message} type={feedback?.type} />

      <Box
        className={styles.tabsContainer}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Tabs
          value={activeCategoryId}
          onChange={handleTabChange}
          className={styles.categoryTabs}
          variant="scrollable"
          scrollButtons="auto"
        >
          {categories.map((cat) => (
            <Tab key={cat.id} label={cat.name} value={cat.id} />
          ))}
        </Tabs>
      </Box>

      <Box className={styles.gridWrapper}>
        <DataGrid
          rows={subCategories}
          columns={columns}
          loading={subCategoriesLoading}
          sx={commonGridStyles}
          disableColumnMenu
        />
      </Box>

      <ConfirmModal
        open={confirmModal.open}
        title="Confirm Save"
        description="Are you sure to save changes?"
        onClose={() => setConfirmModal({ open: false, type: null })}
        onConfirm={confirmSave}
        loading={savingSub}
      />
    </Box>
  );
};

export default PointConfig;
